import React from "react";
import { useTranslation } from "react-i18next";

export default function PastTripCatchDetailsSection({ pastTripCatch }) {
  const { t } = useTranslation();
  const {
    formState,
    modalFieldIdBase,
    fishSuggestions,
    wayOfFishingOptions,
    typeOfFishingOptions,
    styleOptions,
    techniqueOptions,
    rodSuggestions,
    reelSuggestions,
    lureSuggestions,
    baitSuggestions,
    showLureField,
    savedGearSets,
    isLoadingSavedGearSets,
    savedGearSetLoadError,
    shouldShowSavedGearSelector,
    handleFieldChange,
    handleWayChange,
    handleTypeChange,
    handleStyleChange,
    handleSavedGearSetChange,
  } = pastTripCatch;

  return (
    <>
      <label className="mytrips-trip-catch-field mytrips-trip-catch-field--full">
        <span className="mytrips-trip-catch-label">
          {t("fish", { defaultValue: "Fish" })} *
        </span>
        <input
          type="text"
          className="mytrips-trip-catch-input"
          list={`${modalFieldIdBase}-fish-options`}
          value={formState.fish}
          onChange={handleFieldChange("fish")}
          placeholder={t("catch_fish_species_placeholder", {
            defaultValue: "Start typing fish species...",
          })}
          required
        />
        {fishSuggestions.length > 0 ? (
          <datalist id={`${modalFieldIdBase}-fish-options`}>
            {fishSuggestions.map((fishName) => (
              <option key={fishName} value={fishName} />
            ))}
          </datalist>
        ) : null}
      </label>

      <label className="mytrips-trip-catch-field">
        <span className="mytrips-trip-catch-label">
          {t("size", { defaultValue: "Size" })}
        </span>
        <input
          type="text"
          className="mytrips-trip-catch-input"
          value={formState.size}
          onChange={handleFieldChange("size")}
          placeholder={t("catch_size_placeholder", {
            defaultValue: "e.g., 60 cm",
          })}
        />
      </label>

      <label className="mytrips-trip-catch-field">
        <span className="mytrips-trip-catch-label">
          {t("weight", { defaultValue: "Weight" })}
        </span>
        <input
          type="text"
          className="mytrips-trip-catch-input"
          value={formState.weight}
          onChange={handleFieldChange("weight")}
          placeholder={t("catch_weight_placeholder", {
            defaultValue: "e.g., 3 kg",
          })}
        />
      </label>

      <label className="mytrips-trip-catch-field">
        <span className="mytrips-trip-catch-label">
          {t("catch_way_of_fishing", {
            defaultValue: "Way of Fishing",
          })}
        </span>
        <select
          className="mytrips-trip-catch-select"
          value={formState.wayOfFishing}
          onChange={handleWayChange}
        >
          <option value="">{t("select", { defaultValue: "Select" })}</option>
          {wayOfFishingOptions.map((way) => (
            <option key={way} value={way}>
              {way === "Shore"
                ? t("fishing_way_shore", { defaultValue: way })
                : way === "Boat"
                ? t("fishing_way_boat", { defaultValue: way })
                : way}
            </option>
          ))}
        </select>
      </label>

      <label className="mytrips-trip-catch-field">
        <span className="mytrips-trip-catch-label">
          {t("catch_type_of_fishing", {
            defaultValue: "Type of Fishing",
          })}
        </span>
        <select
          className="mytrips-trip-catch-select"
          value={formState.typeOfFishing}
          onChange={handleTypeChange}
          disabled={typeOfFishingOptions.length === 0}
        >
          <option value="">
            {t("select_type_of_fishing", {
              defaultValue: "Select type of fishing",
            })}
          </option>
          {typeOfFishingOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </label>

      {(styleOptions.length > 0 || formState.style) && (
        <label className="mytrips-trip-catch-field">
          <span className="mytrips-trip-catch-label">
            {t("catch_style", {
              defaultValue: "Style",
            })}
          </span>
          <select
            className="mytrips-trip-catch-select"
            value={formState.style}
            onChange={handleStyleChange}
            disabled={styleOptions.length === 0}
          >
            <option value="">
              {t("select_style", { defaultValue: "Select style" })}
            </option>
            {styleOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
      )}

      {(techniqueOptions.length > 0 || formState.technique) && (
        <label className="mytrips-trip-catch-field">
          <span className="mytrips-trip-catch-label">
            {t("catch_technique", {
              defaultValue: "Technique",
            })}
          </span>
          <select
            className="mytrips-trip-catch-select"
            value={formState.technique}
            onChange={handleFieldChange("technique")}
            disabled={techniqueOptions.length === 0}
          >
            <option value="">
              {t("select_technique", {
                defaultValue: "Select technique",
              })}
            </option>
            {techniqueOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
      )}

      {shouldShowSavedGearSelector ? (
        <label className="mytrips-trip-catch-field mytrips-trip-catch-field--full">
          <span className="mytrips-trip-catch-label">
            {t("catch_saved_gear_label")}
          </span>
          <select
            className="mytrips-trip-catch-select"
            value={formState.savedGearSetId}
            onChange={handleSavedGearSetChange}
            disabled={
              isLoadingSavedGearSets ||
              Boolean(savedGearSetLoadError) ||
              savedGearSets.length === 0
            }
          >
            <option value="">
              {isLoadingSavedGearSets
                ? t("loading")
                : savedGearSets.length > 0
                ? t("catch_saved_gear_placeholder")
                : t("catch_saved_gear_none")}
            </option>
            {savedGearSets.map((gearSet) => (
              <option key={gearSet.id} value={gearSet.id}>
                {gearSet.name}
              </option>
            ))}
          </select>
          {savedGearSetLoadError ? (
            <span className="mytrips-trip-catch-error-text">
              {t(savedGearSetLoadError)}
            </span>
          ) : null}
        </label>
      ) : null}

      <label className="mytrips-trip-catch-field">
        <span className="mytrips-trip-catch-label">
          {t("rod", { defaultValue: "Rod" })}
        </span>
        <input
          type="text"
          className="mytrips-trip-catch-input"
          list={`${modalFieldIdBase}-rod-options`}
          value={formState.rod}
          onChange={handleFieldChange("rod")}
          placeholder={t("catch_enter_rod")}
        />
        {rodSuggestions.length > 0 ? (
          <datalist id={`${modalFieldIdBase}-rod-options`}>
            {rodSuggestions.map((rodOption) => (
              <option key={rodOption} value={rodOption} />
            ))}
          </datalist>
        ) : null}
      </label>

      <label className="mytrips-trip-catch-field">
        <span className="mytrips-trip-catch-label">
          {t("reel", { defaultValue: "Reel" })}
        </span>
        <input
          type="text"
          className="mytrips-trip-catch-input"
          list={`${modalFieldIdBase}-reel-options`}
          value={formState.reel}
          onChange={handleFieldChange("reel")}
          placeholder={t("catch_enter_reel")}
        />
        {reelSuggestions.length > 0 ? (
          <datalist id={`${modalFieldIdBase}-reel-options`}>
            {reelSuggestions.map((reelOption) => (
              <option key={reelOption} value={reelOption} />
            ))}
          </datalist>
        ) : null}
      </label>

      {showLureField ? (
        <label className="mytrips-trip-catch-field">
          <span className="mytrips-trip-catch-label">
            {t("lure", { defaultValue: "Lure" })}
          </span>
          <input
            type="text"
            className="mytrips-trip-catch-input"
            list={`${modalFieldIdBase}-lure-options`}
            value={formState.lure}
            onChange={handleFieldChange("lure")}
            placeholder={t("enter_lure", {
              defaultValue: "Enter lure...",
            })}
          />
          {lureSuggestions.length > 0 ? (
            <datalist id={`${modalFieldIdBase}-lure-options`}>
              {lureSuggestions.map((lureOption) => (
                <option key={lureOption} value={lureOption} />
              ))}
            </datalist>
          ) : null}
        </label>
      ) : (
        <label className="mytrips-trip-catch-field">
          <span className="mytrips-trip-catch-label">
            {t("bait", { defaultValue: "Bait" })}
          </span>
          <input
            type="text"
            className="mytrips-trip-catch-input"
            list={`${modalFieldIdBase}-bait-options`}
            value={formState.bait}
            onChange={handleFieldChange("bait")}
            placeholder={t("enter_bait_used", {
              defaultValue: "Enter bait used...",
            })}
          />
          {baitSuggestions.length > 0 ? (
            <datalist id={`${modalFieldIdBase}-bait-options`}>
              {baitSuggestions.map((baitOption) => (
                <option key={baitOption} value={baitOption} />
              ))}
            </datalist>
          ) : null}
        </label>
      )}

      <label className="mytrips-trip-catch-field mytrips-trip-catch-field--full">
        <span className="mytrips-trip-catch-label">
          {t("trip_notes", { defaultValue: "Notes" })}
        </span>
        <textarea
          className="mytrips-trip-catch-textarea"
          value={formState.notes}
          onChange={handleFieldChange("notes")}
          placeholder={t("trip_notes_placeholder", {
            defaultValue: "Add any extra notes here...",
          })}
        />
      </label>
    </>
  );
}
